import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import syncTemplatesService from '../services/syncTemplatesService';
import accountService from '../services/accountService';
import hotelService from '../services/hotelService';
import CountrySelect from './CountrySelect';
import countryService from '../services/countryService';
import './AdminSyncTemplates.css';
import './PaymentPorterPage.css';
import { prettyJson } from '../utils/prettyJson';
import { formatDateTime } from '../utils/datetime';
import { ProcessLambdaSettings } from './shared/StepSettingsEditor';

const PHASE_OPTIONS  = ['CONFIG', 'EXTRACT', 'TRANSFORM', 'LOAD', 'SYNC', 'PROCESS', 'SCHEDULE', ''];
const TARGET_OPTIONS = ['API', 'CONNECTIVITY', 'CONVERSION', 'SOURCE', 'TARGET', ''];

// The four standing EventBridge pollers a process can attach to. Values are the rule suffixes from
// local.poller_schedules in terraform/pollers.tf and the literal pollerFrequency each function is given
// — the poller matches on equality, so these strings are a contract, not labels.
//
// '' (Not scheduled) is FIRST and is the default for every new process: a process runs only from the
// Run Process button unless someone deliberately puts it on a poller. Attaching a task to a schedule
// should be a decision, never something that happens because a form defaulted to it.
const POLLER_FREQUENCY_OPTIONS = [
    { value: '',       label: '— Not scheduled (manual only) —' },
    { value: '1-min',  label: 'Every minute' },
    { value: '5-min',  label: 'Every 5 minutes' },
    { value: '60-min', label: 'Hourly' },
    { value: '1-day',  label: 'Daily' },
];
const pollerFrequencyLabel = (v) =>
    (POLLER_FREQUENCY_OPTIONS.find(o => o.value === (v || ''))?.label) || v;

const emptyTask = { name: '', description: '', sequence: 1, automaticYn: 'N', phase: '', cronSchedule: '', target: '', repeatable: 'N', details: '', scopeId: '', showInstructionButtonYn: 'N', showProcessButtonYn: 'N', filePrefix: '', isFileBasedConversion: 'N', multipleFileFromSync: 'Y', usePresignedUrlUpload: 'N', isExcelConversionCodeTask: 'N', excelCopy: 'N', processWarning: '' };
const emptyInstruction = { title: '', instruction: '', countryCodes: [], accountIds: [], hotelIds: [] };
const emptyProcess = { name: '', lambdaName: '', pollerFrequency: '', countryCodes: [], accountIds: [], hotelIds: [] };

const AdminSyncTaskForm = () => {
    const navigate         = useNavigate();
    const { id, taskId }   = useParams();
    const isNew            = !taskId;

    const [form, setForm]   = useState(emptyTask);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving]   = useState(false);
    const [error, setError]     = useState(null);

    // ── Main tab (Instructions / Processes) ───────────────────────────────────
    const [mainTab, setMainTab] = useState('instructions');

    // ── Instructions ──────────────────────────────────────────────────────────
    const [instructions, setInstructions]             = useState([]);
    const [instrForm, setInstrForm]                   = useState(null);
    const [instrEditId, setInstrEditId]               = useState(null);
    const [instrSaving, setInstrSaving]               = useState(false);
    const [instrDeleteConfirm, setInstrDeleteConfirm] = useState(null);
    const [instrTab, setInstrTab]                     = useState('country');
    const [addingInTab, setAddingInTab]               = useState(false);
    const [sectionSearch, setSectionSearch]           = useState('');

    // ── Processes ─────────────────────────────────────────────────────────────
    const [processes, setProcesses]                   = useState([]);
    const [procForm, setProcForm]                     = useState(null);
    const [procEditId, setProcEditId]                 = useState(null);
    const [procSaving, setProcSaving]                 = useState(false);
    const [procDeleteConfirm, setProcDeleteConfirm]   = useState(null);
    const [procTab, setProcTab]                       = useState('country');
    const [addingInProcTab, setAddingInProcTab]       = useState(false);
    const [procSectionSearch, setProcSectionSearch]   = useState('');

    // ── Shared lookup data ────────────────────────────────────────────────────
    const [allScopes, setAllScopes]       = useState([]);
    const [allCountries, setAllCountries] = useState([]);
    const [allChains, setAllChains]       = useState([]);
    const [allHotels, setAllHotels]       = useState([]);
    const [hotelsLoading, setHotelsLoading] = useState(false);
    const [procAllHotels, setProcAllHotels]       = useState([]);
    const [procHotelsLoading, setProcHotelsLoading] = useState(false);
    const hotelSearchTimer = React.useRef(null);
    const procHotelSearchTimer = React.useRef(null);

    useEffect(() => {
        syncTemplatesService.listScopes().then(scopes => {
            setAllScopes(scopes || []);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        countryService.getAllCountries().then(res => {
            setAllCountries(res.data?.countries || []);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        accountService.getAllAccounts({ activeOnly: false }).then(data => {
            const accounts = data.accounts || data || [];
            setAllChains(accounts.map(a => ({ value: a.id, label: a.name || String(a.id) })));
        }).catch(() => {});
    }, []);

    // Hotel search for instructions
    useEffect(() => {
        if (instrTab !== 'hotel' || !addingInTab) return;
        clearTimeout(hotelSearchTimer.current);
        if (sectionSearch.length < 2) { setAllHotels([]); return; }
        hotelSearchTimer.current = setTimeout(() => {
            setHotelsLoading(true);
            hotelService.getAllHotels({ search: sectionSearch, limit: 50 })
                .then(d => setAllHotels((d.hotels || []).map(h => ({ value: h.hotelid, label: h.hotelname || String(h.hotelid) }))))
                .catch(() => {})
                .finally(() => setHotelsLoading(false));
        }, 300);
    }, [sectionSearch, instrTab, addingInTab]); // eslint-disable-line

    // Hotel search for processes
    useEffect(() => {
        if (procTab !== 'hotel' || !addingInProcTab) return;
        clearTimeout(procHotelSearchTimer.current);
        if (procSectionSearch.length < 2) { setProcAllHotels([]); return; }
        procHotelSearchTimer.current = setTimeout(() => {
            setProcHotelsLoading(true);
            hotelService.getAllHotels({ search: procSectionSearch, limit: 50 })
                .then(d => setProcAllHotels((d.hotels || []).map(h => ({ value: h.hotelid, label: h.hotelname || String(h.hotelid) }))))
                .catch(() => {})
                .finally(() => setProcHotelsLoading(false));
        }, 300);
    }, [procSectionSearch, procTab, addingInProcTab]); // eslint-disable-line

    useEffect(() => {
        if (!isNew) {
            Promise.all([
                syncTemplatesService.getTask(id, taskId),
                syncTemplatesService.listInstructions(id, taskId),
                syncTemplatesService.listProcesses(id, taskId),
            ])
                .then(([data, instrList, procList]) => {
                    const t = data.task || data;
                    setForm({
                        name:        t.name || '',
                        description: t.description || '',
                        sequence:    t.sequence || 1,
                        automaticYn: t.automaticYn || 'N',
                        phase:       t.phase || '',
                        cronSchedule: t.cronSchedule || '',
                        target:      t.target || '',
                        repeatable:           t.repeatable || 'N',
                        details:              t.details || '',
                        scopeId:              t.scopeId || '',
                        filePrefix:           t.filePrefix || '',
                        isFileBasedConversion: t.isFileBasedConversion || 'N',
                        multipleFileFromSync:  t.multipleFileFromSync || 'Y',
                        // Defaults to N, which is what every task created before this existed does.
                        usePresignedUrlUpload: t.usePresignedUrlUpload || 'N',
                        isExcelConversionCodeTask: t.isExcelConversionCodeTask || 'N',
                        excelCopy: t.excelCopy || 'N',
                        processWarning:        t.processWarning || '',
                        showInstructionButtonYn: t.showInstructionButtonYn || 'N',
                        showProcessButtonYn:     t.showProcessButtonYn || 'N',
                    });
                    setInstructions((instrList || []).map(i => ({
                        ...i,
                        isDefault:    !!i.isDefault,
                        countryCodes: i.countryCodes ? i.countryCodes.split(',') : [],
                        accountIds:   i.accountIds   ? i.accountIds.split(',').map(Number) : [],
                        hotelIds:     i.hotelIds     ? i.hotelIds.split(',').map(Number)   : [],
                    })));
                    setProcesses((procList || []).map(p => ({
                        ...p,
                        isDefault:    !!p.isDefault,
                        lambdaName:   p.lambdaName || '',
                        countryCodes: p.countryCodes ? p.countryCodes.split(',') : [],
                        accountIds:   p.accountIds   ? p.accountIds.split(',').map(Number) : [],
                        hotelIds:     p.hotelIds     ? p.hotelIds.split(',').map(Number)   : [],
                    })));
                })
                .catch(() => setError('Failed to load task'))
                .finally(() => setLoading(false));
        }
    }, [id, taskId, isNew]);

    // ── Instruction handlers ───────────────────────────────────────────────────

    const setInstr = (field, value) => setInstrForm(f => ({ ...f, [field]: value }));

    const handleInstrSave = async () => {
        if (!instrForm.title.trim()) return;
        setInstrSaving(true);
        try {
            if (instrEditId === null) {
                const created = await syncTemplatesService.createInstruction(id, taskId, instrForm);
                if (instructions.length === 0) {
                    await syncTemplatesService.setDefaultInstruction(id, taskId, created.id);
                }
            } else {
                await syncTemplatesService.updateInstruction(id, taskId, instrEditId, instrForm);
            }
            const updated = await syncTemplatesService.listInstructions(id, taskId);
            setInstructions((updated || []).map(i => ({
                ...i,
                isDefault:    !!i.isDefault,
                countryCodes: i.countryCodes ? i.countryCodes.split(',') : [],
                accountIds:   i.accountIds   ? i.accountIds.split(',').map(Number) : [],
                hotelIds:     i.hotelIds     ? i.hotelIds.split(',').map(Number)   : [],
            })));
            setInstrForm(null);
            setInstrEditId(null);
            setAddingInTab(false);
            setSectionSearch('');
        } catch (err) {
            setError(err.response?.data?.message || 'Save failed');
        } finally {
            setInstrSaving(false);
        }
    };

    const handleSetDefault = async (instrId) => {
        try {
            await syncTemplatesService.setDefaultInstruction(id, taskId, instrId);
            setInstructions(prev => prev.map(i => ({ ...i, isDefault: i.id === instrId })));
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to set default');
        }
    };

    const handleInstrDelete = async () => {
        if (!instrDeleteConfirm) return;
        try {
            await syncTemplatesService.deleteInstruction(id, taskId, instrDeleteConfirm.id);
            setInstructions(prev => prev.filter(i => i.id !== instrDeleteConfirm.id));
        } catch (err) {
            setError(err.response?.data?.message || 'Delete failed');
        } finally {
            setInstrDeleteConfirm(null);
        }
    };

    // ── Process handlers ───────────────────────────────────────────────────────

    const setProc = (field, value) => setProcForm(f => ({ ...f, [field]: value }));

    const handleProcSave = async () => {
        if (!procForm.name.trim()) return;
        setProcSaving(true);
        try {
            if (procEditId === null) {
                const created = await syncTemplatesService.createProcess(id, taskId, procForm);
                if (processes.length === 0) {
                    await syncTemplatesService.setDefaultProcess(id, taskId, created.id);
                }
            } else {
                await syncTemplatesService.updateProcess(id, taskId, procEditId, procForm);
            }
            const updated = await syncTemplatesService.listProcesses(id, taskId);
            setProcesses((updated || []).map(p => ({
                ...p,
                isDefault:    !!p.isDefault,
                lambdaName:   p.lambdaName || '',
                countryCodes: p.countryCodes ? p.countryCodes.split(',') : [],
                accountIds:   p.accountIds   ? p.accountIds.split(',').map(Number) : [],
                hotelIds:     p.hotelIds     ? p.hotelIds.split(',').map(Number)   : [],
            })));
            setProcForm(null);
            setProcEditId(null);
            setAddingInProcTab(false);
            setProcSectionSearch('');
        } catch (err) {
            setError(err.response?.data?.message || 'Save failed');
        } finally {
            setProcSaving(false);
        }
    };

    const handleSetDefaultProcess = async (procId) => {
        try {
            await syncTemplatesService.setDefaultProcess(id, taskId, procId);
            setProcesses(prev => prev.map(p => ({ ...p, isDefault: p.id === procId })));
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to set default');
        }
    };

    const handleProcDelete = async () => {
        if (!procDeleteConfirm) return;
        try {
            await syncTemplatesService.deleteProcess(id, taskId, procDeleteConfirm.id);
            setProcesses(prev => prev.filter(p => p.id !== procDeleteConfirm.id));
        } catch (err) {
            setError(err.response?.data?.message || 'Delete failed');
        } finally {
            setProcDeleteConfirm(null);
        }
    };

    const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

    const handleSave = async () => {
        if (!form.name.trim()) return;
        setSaving(true);
        setError(null);
        try {
            if (isNew) {
                await syncTemplatesService.createTask(id, form);
            } else {
                await syncTemplatesService.updateTask(id, taskId, form);
            }
            navigate(`/admin/sync-templates/${id}/edit`);
        } catch (err) {
            setError(err.response?.data?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const effectiveMainTab = mainTab;

    const selectedScope = form.scopeId ? allScopes.find(s => String(s.id) === String(form.scopeId)) : null;

    if (loading) return <div className="ast-container"><div className="ast-loading">Loading…</div></div>;

    return (
        <>
        <div className="ast-container">
            <button className="pp-back-btn" onClick={() => navigate(`/admin/sync-templates/${id}/edit`)}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Edit Template
            </button>

            <div className="ast-header">
                <h1>{isNew ? 'New Task' : 'Edit Task'}</h1>
            </div>

            {error && <div className="ast-error">{error}</div>}

            <div className="ast-form-card">
                <div className="ast-form-group">
                    <label>Name <span style={{color:'#dc2626'}}>*</span></label>
                    <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Task name" />
                </div>
                <div className="ast-form-group">
                    <label>Description</label>
                    <textarea rows={2} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Optional description" />
                </div>
                <div className="ast-form-row">
                    <div className="ast-form-group">
                        <label>Sequence</label>
                        <input type="number" min={1} value={form.sequence} onChange={e => set('sequence', parseInt(e.target.value) || 1)} />
                    </div>
                    <div className="ast-form-group">
                        <label>Phase</label>
                        <select value={form.phase} onChange={e => set('phase', e.target.value)}>
                            {PHASE_OPTIONS.map(p => <option key={p} value={p}>{p || '—'}</option>)}
                        </select>
                    </div>
                    <div className="ast-form-group">
                        <label>Target</label>
                        <select value={form.target} onChange={e => set('target', e.target.value)}>
                            {TARGET_OPTIONS.map(o => <option key={o} value={o}>{o || '—'}</option>)}
                        </select>
                    </div>
                </div>
                {/* Only a SCHEDULE task has a cadence; showing this on a CONFIG or LOAD task would
                    invite someone to fill in a schedule that nothing will ever read. The lambda clears
                    the stored value on any other phase, so hiding it here cannot orphan one. */}
                {form.phase === 'SCHEDULE' && (
                    <div className="ast-form-row">
                        <div className="ast-form-group" style={{ flex: 1 }}>
                            <label>Cron Schedule</label>
                            <input
                                type="text"
                                value={form.cronSchedule}
                                onChange={e => set('cronSchedule', e.target.value)}
                                placeholder="0 6 * * ? *"
                            />
                            <small className="ast-hint">
                                AWS EventBridge cron, without the <code>cron(...)</code> wrapper — six fields,
                                UTC, with <code>?</code> in either day-of-month or day-of-week.
                                e.g. <code>0 6 * * ? *</code> = 06:00 daily. A five-field Unix cron is not valid.
                            </small>
                        </div>
                    </div>
                )}
                <div className="ast-form-row">
                    <div className="ast-form-group">
                        <label>Automatic</label>
                        <select value={form.automaticYn} onChange={e => set('automaticYn', e.target.value)}>
                            <option value="N">No</option>
                            <option value="Y">Yes</option>
                        </select>
                    </div>
                    <div className="ast-form-group">
                        <label>Repeatable</label>
                        <select value={form.repeatable} onChange={e => set('repeatable', e.target.value)}>
                            <option value="N">No</option>
                            <option value="Y">Yes</option>
                        </select>
                    </div>
                    <div className="ast-form-group">
                        <label>Scope</label>
                        <select value={form.scopeId || ''} onChange={e => set('scopeId', e.target.value ? Number(e.target.value) : '')}>
                            <option value="">— No scope —</option>
                            {allScopes.map(s => (
                                <option key={s.id} value={s.id}>{s.scopeType}{s.description ? ` — ${s.description}` : ''}</option>
                            ))}
                        </select>
                    </div>
                </div>
                {selectedScope && (
                    <div className="ast-scope-info">
                        <span className="ast-scope-info-label">Scope Info</span>
                        <span className="ast-scope-info-item"><strong>ID:</strong> {selectedScope.id}</span>
                        <span className="ast-scope-info-item"><strong>Type:</strong> {selectedScope.scopeType}</span>
                        <span className="ast-scope-info-item"><strong>Description:</strong> {selectedScope.description || '—'}</span>
                        <span className="ast-scope-info-item"><strong>Status:</strong> {selectedScope.status || '—'}</span>
                        <span className="ast-scope-info-note">Incoming messages whose dataType matches this scope type are linked to this task.</span>
                    </div>
                )}
                <div className="ast-form-row">
                    <div className="ast-form-group">
                        <label>File Prefix</label>
                        <input value={form.filePrefix} onChange={e => set('filePrefix', e.target.value)} placeholder="Optional prefix for generated files" />
                    </div>
                    <div className="ast-form-group">
                        <label>Is File Based Conversion</label>
                        <select value={form.isFileBasedConversion} onChange={e => set('isFileBasedConversion', e.target.value)}>
                            <option value="N">No</option>
                            <option value="Y">Yes</option>
                        </select>
                    </div>
                    <div className="ast-form-group">
                        <label>Multiple File From Sync</label>
                        <select value={form.multipleFileFromSync} onChange={e => set('multipleFileFromSync', e.target.value)}>
                            <option value="Y">Yes</option>
                            <option value="N">No</option>
                        </select>
                    </div>
                    {/* HOW THE EXTRACT COMES BACK. No by default, and every task that existed before
                        this setting did keeps that: SyncConnect POSTs the file to the hub, which is
                        capped at 6MB by the Lambda behind the API. Yes sends it straight to S3 on a
                        presigned URL instead, for the extracts that do not fit — an AR account file
                        with all its detail rows, where a room list is a few tens of KB. */}
                    <div className="ast-form-group">
                        <label>Use Presigned URL Upload</label>
                        <select value={form.usePresignedUrlUpload} onChange={e => set('usePresignedUrlUpload', e.target.value)}>
                            <option value="N">No</option>
                            <option value="Y">Yes</option>
                        </select>
                        <small className="ast-hint">
                            For extracts too large to post to the hub (over ~6MB). Uploads to S3 and
                            enqueues the key instead of the file.
                        </small>
                    </div>
                    {/* WHICH TASK OWNS THE CONVERSION CODES. A template has one task whose job is the
                        workbook the project's conversion codes come from; saying so here means the screens
                        that deal with those codes find it from the template rather than being told which
                        task it is for every template separately. */}
                    <div className="ast-form-group">
                        <label>Is Excel Conversion Code Task</label>
                        <select value={form.isExcelConversionCodeTask} onChange={e => set('isExcelConversionCodeTask', e.target.value)}>
                            <option value="N">No</option>
                            <option value="Y">Yes</option>
                        </select>
                        <small className="ast-hint">
                            Yes for the task whose uploaded workbook carries this project's conversion
                            codes. No for every other task.
                        </small>
                    </div>
                    {/* AN EXCEL COPY OF EACH EXTRACT. The XML stays the file of record and the only one
                        delivered; the workbook is a download beside it on the Reconciliation screen. */}
                    <div className="ast-form-group">
                        <label>Excel Copy Of Extract</label>
                        <select value={form.excelCopy} onChange={e => set('excelCopy', e.target.value)}>
                            <option value="N">No</option>
                            <option value="Y">Yes</option>
                        </select>
                        <small className="ast-hint">
                            Yes to offer each XML extract as an Excel workbook too ("Link to Excel" on the
                            record). The workbook is never sent to SFTP.
                        </small>
                    </div>
                </div>
                <div className="ast-form-group">
                    <label>Details</label>
                    <textarea rows={4} value={form.details} onChange={e => set('details', e.target.value)} />
                </div>
                <div className="ast-form-group">
                    <label>Process Warning</label>
                    <textarea rows={3} value={form.processWarning}
                        onChange={e => set('processWarning', e.target.value)}
                        placeholder="Optional. Shown as a confirmation before the task's Run Process button executes (e.g. a purge/overwrite warning). Leave blank for a plain 'Run this process?' confirmation." />
                </div>

                {!isNew && (
                    <div style={{borderTop:'1px solid #f3f4f6', marginTop:'20px', paddingTop:'20px'}}>
                        {/* ── Main tab bar ── */}
                        <div className="ast-instr-tab-bar" style={{marginBottom:'16px'}}>
                            <button
                                type="button"
                                className={`ast-instr-tab${effectiveMainTab === 'instructions' ? ' ast-instr-tab-active' : ''}`}
                                onClick={() => setMainTab('instructions')}
                            >
                                Instructions
                                {instructions.length > 0 && <span className="ast-instr-tab-badge">{instructions.length}</span>}
                            </button>
                            <button
                                type="button"
                                className={`ast-instr-tab${effectiveMainTab === 'processes' ? ' ast-instr-tab-active' : ''}`}
                                onClick={() => setMainTab('processes')}
                            >
                                Processes
                                {processes.length > 0 && <span className="ast-instr-tab-badge">{processes.length}</span>}
                            </button>
                        </div>

                        {/* ── Instructions section ── */}
                        {effectiveMainTab === 'instructions' && (
                            <>
                                <div className="ast-tasks-header">
                                    <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                                        <h3 style={{margin:0}}>Instructions</h3>
                                        <button
                                            type="button"
                                            className={`pp-toggle-btn${form.showInstructionButtonYn === 'Y' ? ' pp-toggle-btn-on' : ' pp-toggle-btn-off'}`}
                                            onClick={() => set('showInstructionButtonYn', form.showInstructionButtonYn === 'Y' ? 'N' : 'Y')}
                                        >
                                            <span className="pp-toggle-track"><span className="pp-toggle-thumb" /></span>
                                            <span>Instruction Button</span>
                                        </button>
                                    </div>
                                    <button className="pp-rules-add-btn" onClick={() => { setInstrForm({ ...emptyInstruction }); setInstrEditId(null); setAddingInTab(false); setSectionSearch(''); }}>
                                        <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                                        Add
                                    </button>
                                </div>

                                <table className="ast-tasks-table">
                                    <thead>
                                        <tr>
                                            <th>Default</th>
                                            <th>Title</th>
                                            <th>Instruction</th>
                                            <th>Countries</th>
                                            <th>Chains</th>
                                            <th>Hotels</th>
                                            {/* WHEN THE QUERY CHANGED — which copy of an instruction is the newer one
                                                (online template 1 vs offline template 6) is otherwise unanswerable here. */}
                                            <th>Created</th>
                                            <th>Updated</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {instructions.length === 0 ? (
                                            <tr><td colSpan={9} className="ast-no-tasks">No instructions yet.</td></tr>
                                        ) : (
                                            instructions.map(instr => (
                                                <tr key={instr.id}>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className={`ast-default-toggle${instr.isDefault ? ' ast-default-toggle-on' : ''}`}
                                                            title={instr.isDefault ? 'Default instruction' : 'Set as default'}
                                                            onClick={() => !instr.isDefault && handleSetDefault(instr.id)}
                                                        >
                                                            {instr.isDefault ? '★' : '☆'}
                                                        </button>
                                                    </td>
                                                    <td>{instr.title || '—'}</td>
                                                    {/* INDENTED, AND IN A BOX OF ITS OWN. An instruction is
                                                        a JSON document carrying an entire Oracle query;
                                                        as one wrapped paragraph in a table cell it filled
                                                        the screen and could not be read at all. Scrolled
                                                        rather than clipped, so the row stays a row. */}
                                                    <td>
                                                        {instr.instruction
                                                            ? <pre className="ast-instr-json">{prettyJson(instr.instruction)}</pre>
                                                            : '—'}
                                                    </td>
                                                    <td>{instr.countryCodes?.length > 0 ? instr.countryCodes.join(', ') : '—'}</td>
                                                    <td>{instr.accountIds?.length > 0 ? instr.accountIds.length : '—'}</td>
                                                    <td>{instr.hotelIds?.length > 0 ? instr.hotelIds.length : '—'}</td>
                                                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(instr.createdAt)}</td>
                                                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(instr.updatedAt)}</td>
                                                    <td>
                                                        <div className="pp-criteria-row-actions">
                                                            <button className="pp-criteria-add-btn pp-criteria-add-btn-primary pp-criteria-add-btn-icon" title="Edit" onClick={() => { setInstrForm({ title: instr.title || '', instruction: prettyJson(instr.instruction), countryCodes: instr.countryCodes || [], accountIds: instr.accountIds || [], hotelIds: instr.hotelIds || [] }); setInstrEditId(instr.id); setAddingInTab(false); setSectionSearch(''); }}>
                                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                                            </button>
                                                            <button className="pp-criteria-add-btn pp-criteria-add-btn-danger pp-criteria-add-btn-icon" title="Delete" onClick={() => setInstrDeleteConfirm({ id: instr.id, title: instr.title || 'this instruction' })}>
                                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>

                                {instrForm && (
                                    <div className="ast-task-form-panel">
                                        <h4 className="ast-task-form-title">{instrEditId === null ? 'New Instruction' : 'Edit Instruction'}</h4>

                                        <div className="ast-form-group">
                                            <label>Title <span style={{color:'#dc2626'}}>*</span></label>
                                            <input value={instrForm.title} onChange={e => setInstr('title', e.target.value)} />
                                        </div>

                                        <div className="ast-form-group">
                                            <label>Instruction</label>
                                            {/* MONOSPACE AND TALL. Three rows of proportional text for a
                                                document of this size was a keyhole; the JSON is indented
                                                when the row is opened (see the Edit button) so what is in
                                                here is already readable. */}
                                            <textarea
                                                className="ast-instr-textarea"
                                                rows={18}
                                                value={instrForm.instruction}
                                                onChange={e => setInstr('instruction', e.target.value)}
                                                spellCheck={false}
                                            />
                                        </div>

                                        <div className="ast-instr-tabs-wrap">
                                            <div className="ast-instr-tab-bar">
                                                {[
                                                    { key: 'country', label: 'Countries', count: instrForm.countryCodes.length },
                                                    { key: 'chain',   label: 'Chains',    count: instrForm.accountIds.length },
                                                    { key: 'hotel',   label: 'Hotels',    count: instrForm.hotelIds.length },
                                                ].map(t => (
                                                    <button
                                                        key={t.key}
                                                        type="button"
                                                        className={`ast-instr-tab${instrTab === t.key ? ' ast-instr-tab-active' : ''}`}
                                                        onClick={() => { setInstrTab(t.key); setAddingInTab(false); setSectionSearch(''); }}
                                                    >
                                                        {t.label}
                                                        {t.count > 0 && <span className="ast-instr-tab-badge">{t.count}</span>}
                                                    </button>
                                                ))}
                                            </div>

                                            <div className="ast-instr-tab-body">
                                                {instrTab === 'country' && (
                                                    <>
                                                        <div className="ast-instr-tab-actions">
                                                            <button type="button" className="pp-criteria-add-btn pp-criteria-add-btn-primary" onClick={() => { setAddingInTab(v => !v); setSectionSearch(''); }}>
                                                                <svg width="11" height="11" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                                                                Add Country
                                                            </button>
                                                        </div>
                                                        {addingInTab && (
                                                            <div className="ast-instr-add-row">
                                                                <CountrySelect
                                                                    value=""
                                                                    onChange={e => {
                                                                        const cc = e.target.value;
                                                                        if (cc && !instrForm.countryCodes.includes(cc)) {
                                                                            setInstr('countryCodes', [...instrForm.countryCodes, cc]);
                                                                        }
                                                                        setAddingInTab(false);
                                                                        setSectionSearch('');
                                                                    }}
                                                                    placeholder="Select country…"
                                                                />
                                                            </div>
                                                        )}
                                                        <div className="ast-instr-list">
                                                            {instrForm.countryCodes.length === 0
                                                                ? <span className="ast-instr-empty">Any country</span>
                                                                : instrForm.countryCodes.map(cc => {
                                                                    const country = allCountries.find(c => c.code === cc);
                                                                    return (
                                                                        <div key={cc} className="ast-instr-list-row">
                                                                            <span><span className="ast-instr-list-code">{cc}</span> {country?.description || cc}</span>
                                                                            <button type="button" className="ast-instr-list-remove" onClick={() => setInstr('countryCodes', instrForm.countryCodes.filter(c => c !== cc))}>×</button>
                                                                        </div>
                                                                    );
                                                                })
                                                            }
                                                        </div>
                                                    </>
                                                )}

                                                {instrTab === 'chain' && (
                                                    <>
                                                        <div className="tenant-dropdown-header" style={{margin:'-0.75rem -1rem 0.75rem', borderRadius:'0'}}>
                                                            <div className="tenant-search-container">
                                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="tenant-search-icon"><circle cx="7" cy="7" r="4" stroke="#6b7280" strokeWidth="2"/><path d="M10 10l3 3" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/></svg>
                                                                <input type="text" className="tenant-search-input" placeholder="Search chains…" value={sectionSearch} autoFocus onChange={e => setSectionSearch(e.target.value)} />
                                                                {sectionSearch && <button type="button" className="search-clear-btn" onClick={() => setSectionSearch('')}><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 3l8 8M11 3l-8 8" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/></svg></button>}
                                                            </div>
                                                        </div>
                                                        <div className="tenant-dropdown-list" style={{maxHeight:'220px', borderBottom:'1px solid #f3f4f6', marginBottom:'0.75rem'}}>
                                                            {allChains.filter(c => c.label.toLowerCase().includes(sectionSearch.toLowerCase())).slice(0, 30).map(c => {
                                                                const selected = instrForm.accountIds.includes(c.value);
                                                                return (
                                                                    <button key={c.value} type="button"
                                                                        className={`tenant-dropdown-item${selected ? ' active' : ''}`}
                                                                        onClick={() => {
                                                                            if (selected) { setInstrForm(f => ({ ...f, accountIds: f.accountIds.filter(a => a !== c.value) })); }
                                                                            else { setInstrForm(f => ({ ...f, accountIds: [...f.accountIds, c.value] })); }
                                                                        }}
                                                                    >
                                                                        <div className="tenant-item-content"><span className="tenant-item-name">{c.label}</span></div>
                                                                        {selected && <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M13 4L6 11L3 8" stroke="#4F7FFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                                                    </button>
                                                                );
                                                            })}
                                                            {allChains.filter(c => c.label.toLowerCase().includes(sectionSearch.toLowerCase())).length === 0 && (
                                                                <div style={{padding:'1rem', color:'#9ca3af', fontSize:'0.82rem', textAlign:'center'}}>No chains found</div>
                                                            )}
                                                        </div>
                                                        <div className="ast-instr-list">
                                                            {instrForm.accountIds.length === 0
                                                                ? <span className="ast-instr-empty">All chains</span>
                                                                : instrForm.accountIds.map(aid => {
                                                                    const chain = allChains.find(c => c.value === aid);
                                                                    return (
                                                                        <div key={aid} className="ast-instr-list-row">
                                                                            <span>{chain?.label || aid}</span>
                                                                            <button type="button" className="ast-instr-list-remove" onClick={() => setInstrForm(f => ({ ...f, accountIds: f.accountIds.filter(a => a !== aid) }))}>×</button>
                                                                        </div>
                                                                    );
                                                                })
                                                            }
                                                        </div>
                                                    </>
                                                )}

                                                {instrTab === 'hotel' && (
                                                    <>
                                                        <div className="tenant-dropdown-header" style={{margin:'-0.75rem -1rem 0.75rem', borderRadius:'0'}}>
                                                            <div className="tenant-search-container">
                                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="tenant-search-icon"><circle cx="7" cy="7" r="4" stroke="#6b7280" strokeWidth="2"/><path d="M10 10l3 3" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/></svg>
                                                                <input type="text" className="tenant-search-input" placeholder="Type 2+ characters to search hotels…" value={sectionSearch} autoFocus onChange={e => { setSectionSearch(e.target.value); setAddingInTab(true); }} />
                                                                {sectionSearch && <button type="button" className="search-clear-btn" onClick={() => { setSectionSearch(''); setAllHotels([]); }}><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 3l8 8M11 3l-8 8" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/></svg></button>}
                                                            </div>
                                                        </div>
                                                        <div className="tenant-dropdown-list" style={{maxHeight:'220px', borderBottom:'1px solid #f3f4f6', marginBottom:'0.75rem'}}>
                                                            {hotelsLoading && <div style={{padding:'1rem', color:'#9ca3af', fontSize:'0.82rem', textAlign:'center'}}>Searching…</div>}
                                                            {!hotelsLoading && sectionSearch.length < 2 && <div style={{padding:'1rem', color:'#9ca3af', fontSize:'0.82rem', textAlign:'center'}}>Type to search hotels</div>}
                                                            {!hotelsLoading && sectionSearch.length >= 2 && allHotels.slice(0, 30).map(h => {
                                                                const selected = instrForm.hotelIds.includes(h.value);
                                                                return (
                                                                    <button key={h.value} type="button"
                                                                        className={`tenant-dropdown-item${selected ? ' active' : ''}`}
                                                                        onClick={() => {
                                                                            if (selected) { setInstr('hotelIds', instrForm.hotelIds.filter(hid => hid !== h.value)); }
                                                                            else { setInstr('hotelIds', [...instrForm.hotelIds, h.value]); }
                                                                        }}
                                                                    >
                                                                        <div className="tenant-item-content"><span className="tenant-item-name">{h.label}</span></div>
                                                                        {selected && <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M13 4L6 11L3 8" stroke="#4F7FFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                                                    </button>
                                                                );
                                                            })}
                                                            {!hotelsLoading && sectionSearch.length >= 2 && allHotels.length === 0 && (
                                                                <div style={{padding:'1rem', color:'#9ca3af', fontSize:'0.82rem', textAlign:'center'}}>No hotels found</div>
                                                            )}
                                                        </div>
                                                        <div className="ast-instr-list">
                                                            {instrForm.hotelIds.length === 0
                                                                ? <span className="ast-instr-empty">All hotels</span>
                                                                : instrForm.hotelIds.map(hid => {
                                                                    const hotel = allHotels.find(h => h.value === hid);
                                                                    return (
                                                                        <div key={hid} className="ast-instr-list-row">
                                                                            <span>{hotel?.label || hid}</span>
                                                                            <button type="button" className="ast-instr-list-remove" onClick={() => setInstr('hotelIds', instrForm.hotelIds.filter(h => h !== hid))}>×</button>
                                                                        </div>
                                                                    );
                                                                })
                                                            }
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        <div className="ast-form-actions">
                                            <button className="pp-criteria-add-btn" onClick={() => { setInstrForm(null); setInstrEditId(null); setAddingInTab(false); setSectionSearch(''); }} disabled={instrSaving}>
                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" style={{flexShrink:0}}><line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                                                Cancel
                                            </button>
                                            <button className="pp-rules-add-btn" onClick={handleInstrSave} disabled={instrSaving || !instrForm.title.trim()}>
                                                {instrSaving
                                                    ? <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="pp-preview-spinner" style={{flexShrink:0}}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.3"/><path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>Saving…</>
                                                    : <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{flexShrink:0}}><polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>Save</>
                                                }
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* ── Processes section ── */}
                        {effectiveMainTab === 'processes' && (
                            <>
                                <div className="ast-tasks-header">
                                    <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                                        <h3 style={{margin:0}}>Processes</h3>
                                        <button
                                            type="button"
                                            className={`pp-toggle-btn${form.showProcessButtonYn === 'Y' ? ' pp-toggle-btn-on' : ' pp-toggle-btn-off'}`}
                                            onClick={() => set('showProcessButtonYn', form.showProcessButtonYn === 'Y' ? 'N' : 'Y')}
                                        >
                                            <span className="pp-toggle-track"><span className="pp-toggle-thumb" /></span>
                                            <span>Process Button</span>
                                        </button>
                                    </div>
                                    <button className="pp-rules-add-btn" onClick={() => { setProcForm({ ...emptyProcess }); setProcEditId(null); setAddingInProcTab(false); setProcSectionSearch(''); }}>
                                        <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                                        Add
                                    </button>
                                </div>

                                <table className="ast-tasks-table">
                                    <thead>
                                        <tr>
                                            <th>Default</th>
                                            <th>Name</th>
                                            <th>Lambda Name</th>
                                            <th>Schedule</th>
                                            <th>Countries</th>
                                            <th>Chains</th>
                                            <th>Hotels</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {processes.length === 0 ? (
                                            <tr><td colSpan={8} className="ast-no-tasks">No processes yet.</td></tr>
                                        ) : (
                                            processes.map(proc => (
                                                <tr key={proc.id}>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className={`ast-default-toggle${proc.isDefault ? ' ast-default-toggle-on' : ''}`}
                                                            title={proc.isDefault ? 'Default process' : 'Set as default'}
                                                            onClick={() => !proc.isDefault && handleSetDefaultProcess(proc.id)}
                                                        >
                                                            {proc.isDefault ? '★' : '☆'}
                                                        </button>
                                                    </td>
                                                    <td>{proc.name || '—'}</td>
                                                    <td>{proc.lambdaName || '—'}</td>
                                                    <td>
                                                        {proc.pollerFrequency && form.phase === 'SCHEDULE'
                                                            ? <span className="ast-sched-chip">{pollerFrequencyLabel(proc.pollerFrequency)}</span>
                                                            : <span className="ast-sched-manual">Manual</span>}
                                                    </td>
                                                    <td>{proc.countryCodes?.length > 0 ? proc.countryCodes.join(', ') : '—'}</td>
                                                    <td>{proc.accountIds?.length > 0 ? proc.accountIds.length : '—'}</td>
                                                    <td>{proc.hotelIds?.length > 0 ? proc.hotelIds.length : '—'}</td>
                                                    <td>
                                                        <div className="pp-criteria-row-actions">
                                                            <button className="pp-criteria-add-btn pp-criteria-add-btn-primary pp-criteria-add-btn-icon" title="Edit" onClick={() => { setProcForm({ name: proc.name || '', lambdaName: proc.lambdaName || '', pollerFrequency: proc.pollerFrequency || '', countryCodes: proc.countryCodes || [], accountIds: proc.accountIds || [], hotelIds: proc.hotelIds || [] }); setProcEditId(proc.id); setAddingInProcTab(false); setProcSectionSearch(''); }}>
                                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                                            </button>
                                                            <button className="pp-criteria-add-btn pp-criteria-add-btn-danger pp-criteria-add-btn-icon" title="Delete" onClick={() => setProcDeleteConfirm({ id: proc.id, name: proc.name || 'this process' })}>
                                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>

                                {procForm && (
                                    <div className="ast-task-form-panel">
                                        <h4 className="ast-task-form-title">{procEditId === null ? 'New Process' : 'Edit Process'}</h4>

                                        <div className="ast-form-row">
                                            <div className="ast-form-group">
                                                <label>Name <span style={{color:'#dc2626'}}>*</span></label>
                                                <input value={procForm.name} onChange={e => setProc('name', e.target.value)} />
                                            </div>
                                            <div className="ast-form-group">
                                                <label>Lambda Name</label>
                                                <input value={procForm.lambdaName} onChange={e => setProc('lambdaName', e.target.value)} placeholder="e.g. my-lambda-function" />
                                            </div>
                                            {/* Only a SCHEDULE task can be put on a poller. Offering it on a CONFIG or
                                                LOAD task would invite someone to schedule work the pipeline expects a
                                                person to sequence — and the phase is the field that already says which
                                                kind of task this is. Disabled rather than hidden so the rule is visible
                                                where the question is asked, instead of the control silently vanishing.
                                                The lambda clears the value on any other phase, so this cannot orphan one. */}
                                            <div className="ast-form-group">
                                                <label>Schedule</label>
                                                <select
                                                    value={form.phase === 'SCHEDULE' ? (procForm.pollerFrequency || '') : ''}
                                                    onChange={e => setProc('pollerFrequency', e.target.value)}
                                                    disabled={form.phase !== 'SCHEDULE'}
                                                >
                                                    {POLLER_FREQUENCY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                </select>
                                                <small className="ast-hint">
                                                    {form.phase === 'SCHEDULE' ? (
                                                        <>
                                                            Runs this process on one of the four standing pollers, once for every
                                                            active project whose task list includes this task. Leave it on
                                                            <strong> Not scheduled</strong> to keep it manual — the Run Process
                                                            button works either way.
                                                        </>
                                                    ) : (
                                                        <>Only available when the task&apos;s <strong>Phase</strong> is <strong>SCHEDULE</strong>. This task is <strong>{form.phase || '—'}</strong>, so its processes run from the Run Process button only.</>
                                                    )}
                                                </small>
                                            </div>
                                        </div>

                                        <div className="ast-instr-tabs-wrap">
                                            <div className="ast-instr-tab-bar">
                                                {[
                                                    { key: 'country', label: 'Countries', count: procForm.countryCodes.length },
                                                    { key: 'chain',   label: 'Chains',    count: procForm.accountIds.length },
                                                    { key: 'hotel',   label: 'Hotels',    count: procForm.hotelIds.length },
                                                ].map(t => (
                                                    <button
                                                        key={t.key}
                                                        type="button"
                                                        className={`ast-instr-tab${procTab === t.key ? ' ast-instr-tab-active' : ''}`}
                                                        onClick={() => { setProcTab(t.key); setAddingInProcTab(false); setProcSectionSearch(''); }}
                                                    >
                                                        {t.label}
                                                        {t.count > 0 && <span className="ast-instr-tab-badge">{t.count}</span>}
                                                    </button>
                                                ))}
                                            </div>

                                            <div className="ast-instr-tab-body">
                                                {procTab === 'country' && (
                                                    <>
                                                        <div className="ast-instr-tab-actions">
                                                            <button type="button" className="pp-criteria-add-btn pp-criteria-add-btn-primary" onClick={() => { setAddingInProcTab(v => !v); setProcSectionSearch(''); }}>
                                                                <svg width="11" height="11" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                                                                Add Country
                                                            </button>
                                                        </div>
                                                        {addingInProcTab && (
                                                            <div className="ast-instr-add-row">
                                                                <CountrySelect
                                                                    value=""
                                                                    onChange={e => {
                                                                        const cc = e.target.value;
                                                                        if (cc && !procForm.countryCodes.includes(cc)) {
                                                                            setProc('countryCodes', [...procForm.countryCodes, cc]);
                                                                        }
                                                                        setAddingInProcTab(false);
                                                                        setProcSectionSearch('');
                                                                    }}
                                                                    placeholder="Select country…"
                                                                />
                                                            </div>
                                                        )}
                                                        <div className="ast-instr-list">
                                                            {procForm.countryCodes.length === 0
                                                                ? <span className="ast-instr-empty">Any country</span>
                                                                : procForm.countryCodes.map(cc => {
                                                                    const country = allCountries.find(c => c.code === cc);
                                                                    return (
                                                                        <div key={cc} className="ast-instr-list-row">
                                                                            <span><span className="ast-instr-list-code">{cc}</span> {country?.description || cc}</span>
                                                                            <button type="button" className="ast-instr-list-remove" onClick={() => setProc('countryCodes', procForm.countryCodes.filter(c => c !== cc))}>×</button>
                                                                        </div>
                                                                    );
                                                                })
                                                            }
                                                        </div>
                                                    </>
                                                )}

                                                {procTab === 'chain' && (
                                                    <>
                                                        <div className="tenant-dropdown-header" style={{margin:'-0.75rem -1rem 0.75rem', borderRadius:'0'}}>
                                                            <div className="tenant-search-container">
                                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="tenant-search-icon"><circle cx="7" cy="7" r="4" stroke="#6b7280" strokeWidth="2"/><path d="M10 10l3 3" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/></svg>
                                                                <input type="text" className="tenant-search-input" placeholder="Search chains…" value={procSectionSearch} autoFocus onChange={e => setProcSectionSearch(e.target.value)} />
                                                                {procSectionSearch && <button type="button" className="search-clear-btn" onClick={() => setProcSectionSearch('')}><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 3l8 8M11 3l-8 8" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/></svg></button>}
                                                            </div>
                                                        </div>
                                                        <div className="tenant-dropdown-list" style={{maxHeight:'220px', borderBottom:'1px solid #f3f4f6', marginBottom:'0.75rem'}}>
                                                            {allChains.filter(c => c.label.toLowerCase().includes(procSectionSearch.toLowerCase())).slice(0, 30).map(c => {
                                                                const selected = procForm.accountIds.includes(c.value);
                                                                return (
                                                                    <button key={c.value} type="button"
                                                                        className={`tenant-dropdown-item${selected ? ' active' : ''}`}
                                                                        onClick={() => {
                                                                            if (selected) { setProcForm(f => ({ ...f, accountIds: f.accountIds.filter(a => a !== c.value) })); }
                                                                            else { setProcForm(f => ({ ...f, accountIds: [...f.accountIds, c.value] })); }
                                                                        }}
                                                                    >
                                                                        <div className="tenant-item-content"><span className="tenant-item-name">{c.label}</span></div>
                                                                        {selected && <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M13 4L6 11L3 8" stroke="#4F7FFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                                                    </button>
                                                                );
                                                            })}
                                                            {allChains.filter(c => c.label.toLowerCase().includes(procSectionSearch.toLowerCase())).length === 0 && (
                                                                <div style={{padding:'1rem', color:'#9ca3af', fontSize:'0.82rem', textAlign:'center'}}>No chains found</div>
                                                            )}
                                                        </div>
                                                        <div className="ast-instr-list">
                                                            {procForm.accountIds.length === 0
                                                                ? <span className="ast-instr-empty">All chains</span>
                                                                : procForm.accountIds.map(aid => {
                                                                    const chain = allChains.find(c => c.value === aid);
                                                                    return (
                                                                        <div key={aid} className="ast-instr-list-row">
                                                                            <span>{chain?.label || aid}</span>
                                                                            <button type="button" className="ast-instr-list-remove" onClick={() => setProcForm(f => ({ ...f, accountIds: f.accountIds.filter(a => a !== aid) }))}>×</button>
                                                                        </div>
                                                                    );
                                                                })
                                                            }
                                                        </div>
                                                    </>
                                                )}

                                                {procTab === 'hotel' && (
                                                    <>
                                                        <div className="tenant-dropdown-header" style={{margin:'-0.75rem -1rem 0.75rem', borderRadius:'0'}}>
                                                            <div className="tenant-search-container">
                                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="tenant-search-icon"><circle cx="7" cy="7" r="4" stroke="#6b7280" strokeWidth="2"/><path d="M10 10l3 3" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/></svg>
                                                                <input type="text" className="tenant-search-input" placeholder="Type 2+ characters to search hotels…" value={procSectionSearch} autoFocus onChange={e => { setProcSectionSearch(e.target.value); setAddingInProcTab(true); }} />
                                                                {procSectionSearch && <button type="button" className="search-clear-btn" onClick={() => { setProcSectionSearch(''); setProcAllHotels([]); }}><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 3l8 8M11 3l-8 8" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/></svg></button>}
                                                            </div>
                                                        </div>
                                                        <div className="tenant-dropdown-list" style={{maxHeight:'220px', borderBottom:'1px solid #f3f4f6', marginBottom:'0.75rem'}}>
                                                            {procHotelsLoading && <div style={{padding:'1rem', color:'#9ca3af', fontSize:'0.82rem', textAlign:'center'}}>Searching…</div>}
                                                            {!procHotelsLoading && procSectionSearch.length < 2 && <div style={{padding:'1rem', color:'#9ca3af', fontSize:'0.82rem', textAlign:'center'}}>Type to search hotels</div>}
                                                            {!procHotelsLoading && procSectionSearch.length >= 2 && procAllHotels.slice(0, 30).map(h => {
                                                                const selected = procForm.hotelIds.includes(h.value);
                                                                return (
                                                                    <button key={h.value} type="button"
                                                                        className={`tenant-dropdown-item${selected ? ' active' : ''}`}
                                                                        onClick={() => {
                                                                            if (selected) { setProc('hotelIds', procForm.hotelIds.filter(hid => hid !== h.value)); }
                                                                            else { setProc('hotelIds', [...procForm.hotelIds, h.value]); }
                                                                        }}
                                                                    >
                                                                        <div className="tenant-item-content"><span className="tenant-item-name">{h.label}</span></div>
                                                                        {selected && <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M13 4L6 11L3 8" stroke="#4F7FFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                                                    </button>
                                                                );
                                                            })}
                                                            {!procHotelsLoading && procSectionSearch.length >= 2 && procAllHotels.length === 0 && (
                                                                <div style={{padding:'1rem', color:'#9ca3af', fontSize:'0.82rem', textAlign:'center'}}>No hotels found</div>
                                                            )}
                                                        </div>
                                                        <div className="ast-instr-list">
                                                            {procForm.hotelIds.length === 0
                                                                ? <span className="ast-instr-empty">All hotels</span>
                                                                : procForm.hotelIds.map(hid => {
                                                                    const hotel = procAllHotels.find(h => h.value === hid);
                                                                    return (
                                                                        <div key={hid} className="ast-instr-list-row">
                                                                            <span>{hotel?.label || hid}</span>
                                                                            <button type="button" className="ast-instr-list-remove" onClick={() => setProc('hotelIds', procForm.hotelIds.filter(h => h !== hid))}>×</button>
                                                                        </div>
                                                                    );
                                                                })
                                                            }
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        <div className="ast-form-actions">
                                            <button className="pp-criteria-add-btn" onClick={() => { setProcForm(null); setProcEditId(null); setAddingInProcTab(false); setProcSectionSearch(''); }} disabled={procSaving}>
                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" style={{flexShrink:0}}><line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                                                Cancel
                                            </button>
                                            <button className="pp-rules-add-btn" onClick={handleProcSave} disabled={procSaving || !procForm.name.trim()}>
                                                {procSaving
                                                    ? <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="pp-preview-spinner" style={{flexShrink:0}}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.3"/><path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>Saving…</>
                                                    : <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{flexShrink:0}}><polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>Save</>
                                                }
                                            </button>
                                        </div>

                                        {/* THE LAMBDA'S SETTINGS — prompted for on Run Process, read by the lambda (processingstepsettings
                                            of the step named after it). Saved on their own, so only for a saved process's lambda. */}
                                        {procEditId !== null && (
                                            <ProcessLambdaSettings lambdaName={(processes.find(p => p.id === procEditId) || {}).lambdaName} />
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                <div className="ast-form-actions">
                    <button className="pp-criteria-add-btn" onClick={() => navigate(`/admin/sync-templates/${id}/edit`)} disabled={saving}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" style={{flexShrink:0}}><line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                        Cancel
                    </button>
                    <button className="pp-rules-add-btn" onClick={handleSave} disabled={saving || !form.name.trim()}>
                        {saving
                            ? <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="pp-preview-spinner" style={{flexShrink:0}}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.3"/><path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>Saving…</>
                            : <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{flexShrink:0}}><polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>{isNew ? 'Create' : 'Save'}</>
                        }
                    </button>
                </div>
            </div>
        </div>

            {instrDeleteConfirm && (
                <div className="ast-modal-overlay">
                    <div className="ast-modal ast-modal-sm">
                        <h2>Delete Instruction</h2>
                        <p>Delete <strong>{instrDeleteConfirm.title}</strong>?</p>
                        <div className="ast-modal-actions">
                            <button className="pp-back-btn" onClick={() => setInstrDeleteConfirm(null)}>Cancel</button>
                            <button className="pp-criteria-add-btn pp-criteria-add-btn-danger" onClick={handleInstrDelete}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {procDeleteConfirm && (
                <div className="ast-modal-overlay">
                    <div className="ast-modal ast-modal-sm">
                        <h2>Delete Process</h2>
                        <p>Delete <strong>{procDeleteConfirm.name}</strong>?</p>
                        <div className="ast-modal-actions">
                            <button className="pp-back-btn" onClick={() => setProcDeleteConfirm(null)}>Cancel</button>
                            <button className="pp-criteria-add-btn pp-criteria-add-btn-danger" onClick={handleProcDelete}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AdminSyncTaskForm;
